import { useTranslations } from '@vocab/react';
import {
  Columns,
  Column,
  Stack,
  Heading,
  Actions,
  Box,
  Text,
  IconArrow,
  Bleed,
} from 'braid-design-system';

import { analyticsEvents } from 'src/constants/analytics';
import {
  TRACKING_DASHBOARD_HERO,
  TRACKING_DASHBOARD_MAIN,
} from 'src/constants/tracking';
import { getLocale, getSite } from 'src/utils/config';
import { getCreateNewJobUrl, getHowToWriteJobAdUrl } from 'src/utils/getUrls';
import { isEmployerANZSite } from 'src/utils/isEmployerANZSite';

import recentJobsTranslations from '../RecentJobs/.vocab';
import TrackingButtonLink from '../TrackingButtonLink/TrackingButtonLink';

import translations from './.vocab';
import type { HeroContentProps } from './Hero';
import CajaUIEN from './PNGs/caja-en.png';
import CajaUIID from './PNGs/caja-id.png';
import CajaUITH from './PNGs/caja-th.png';
import { PinkDonutHalfSvg } from './SVGs/PinkDonutHalfSvg';

import * as styles from './Hero.css';

export const NewHirerHero = ({
  firstName,
  hasCreateJobsPermission,
}: HeroContentProps) => {
  const { t } = useTranslations(translations);
  const { t: jobTrans } = useTranslations(recentJobsTranslations);

  const site = getSite();
  const locale = getLocale();
  const isAnz = isEmployerANZSite(site);

  const createNewJobUrl = getCreateNewJobUrl();

  let cajaUI;
  switch (locale) {
    case 'id-ID':
      cajaUI = CajaUIID;
      break;
    case 'th-TH':
      cajaUI = CajaUITH;
      break;
    default:
      cajaUI = CajaUIEN;
      break;
  }

  return (
    <Columns
      space={{ mobile: 'none', desktop: 'xxxlarge' }}
      collapseBelow="desktop"
    >
      <Column width="1/2">
        <Stack space="medium">
          <Heading level="3" component="p">
            {t('Greeting message')} {firstName}
          </Heading>

          <Text size="large">
            {t('You are in the right place')}
            {hasCreateJobsPermission ? (
              <> {t('Get started by creating first job ad')}</>
            ) : null}
          </Text>

          <Actions>
            {hasCreateJobsPermission && (
              <TrackingButtonLink
                variant="solid"
                tone="brandAccent"
                href={createNewJobUrl}
                trackingEventName={analyticsEvents.TRACK_CREATE_JOB}
                trackingActionOrigin={TRACKING_DASHBOARD_MAIN}
                trackingData={{ isPrimaryCTA: true }}
              >
                {jobTrans('Create a job ad button')}
              </TrackingButtonLink>
            )}
            {hasCreateJobsPermission && isAnz && (
              <TrackingButtonLink
                href={getHowToWriteJobAdUrl()}
                variant="transparent"
                trackingEventName={analyticsEvents.TRACK_WRITE_GREAT_JOB_AD}
                trackingActionOrigin={TRACKING_DASHBOARD_HERO}
                trackingData={{
                  elementAction: 'internal redirect',
                  elementDesign: 'link',
                  elementText: 'how to write a great job ad',
                }}
                icon={<IconArrow direction="right" />}
                iconPosition="trailing"
              >
                {t('How to write a job ad')}
              </TrackingButtonLink>
            )}
          </Actions>
        </Stack>
      </Column>
      <Column width="1/2">
        <Box position="relative">
          <Box className={[styles.svgContainer, styles.svgContainerNew]}>
            <PinkDonutHalfSvg />
          </Box>
          {hasCreateJobsPermission && (
            <Bleed
              bottom={{ mobile: 'xxlarge', desktop: 'none' }}
              left={{ mobile: 'gutter', desktop: 'none' }}
              right={{ mobile: 'gutter', desktop: 'none' }}
            >
              <Box
                className={styles.image}
                component="img"
                src={cajaUI}
                alt={t('Caja image alt')}
              />
            </Bleed>
          )}
        </Box>
      </Column>
    </Columns>
  );
};
